import {
  FACE_SHAPES,
  HAIR_COLORS,
  SKIN_TONES,
  nextFloat,
  type Appearance,
  type HairStyleId,
  type RngState,
  type Sex,
} from "@besenliga/shared";

/** Peso de cada corte por sexo, para atletas gerados terem uma mistura plausível. */
const HAIR_WEIGHTS: Record<Sex, Record<HairStyleId, number>> = {
  homem: { careca: 12, raspado: 32, baixinho: 22, repartido: 26, longo: 5, coque: 3 },
  mulher: { careca: 2, raspado: 4, baixinho: 12, repartido: 14, longo: 38, coque: 30 },
};
const BEARD_CHANCE: Record<Sex, number> = { homem: 0.35, mulher: 0 };
const GOGGLES_CHANCE = 0.3;
const GRAY_HAIR_WEIGHT = 0.04;

function pick<T>(rng: RngState, items: readonly T[]): [RngState, T] {
  const [next, f] = nextFloat(rng);
  return [next, items[Math.min(items.length - 1, Math.floor(f * items.length))] as T];
}

function pickWeighted<K extends string>(rng: RngState, weights: Record<K, number>): [RngState, K] {
  const entries = Object.entries(weights) as [K, number][];
  const total = entries.reduce((n, [, w]) => n + w, 0);
  const [next, f] = nextFloat(rng);
  let acc = f * total;
  for (const [key, w] of entries) {
    acc -= w;
    if (acc < 0) return [next, key];
  }
  const last = entries.at(-1);
  if (!last) throw new Error("pickWeighted sem opções");
  return [next, last[0]];
}

/** Aparência aleatória e reproduzível para atletas NPC (item 8.1) e para o botão "aleatório" do criador. */
export function randomAppearance(rng: RngState, sex: Sex): [RngState, Appearance] {
  let r = rng;
  let f: number;
  const [r1, skin] = pick(r, SKIN_TONES);
  const [r2, hairStyle] = pickWeighted(r1, HAIR_WEIGHTS[sex]);
  [r, f] = nextFloat(r2);
  const pool =
    f < GRAY_HAIR_WEIGHT
      ? HAIR_COLORS.filter((c) => c.id === "grisalho" || c.id === "branco")
      : HAIR_COLORS.filter((c) => c.id !== "azul");
  const [r3, hairColor] = pick(r, pool);
  const [r4, face] = pick(r3, FACE_SHAPES);
  [r, f] = nextFloat(r4);
  const beard = f < BEARD_CHANCE[sex];
  [r, f] = nextFloat(r);
  const goggles = f < GOGGLES_CHANCE;
  return [r, { skinTone: skin.id, hairStyle, hairColor: hairColor.id, faceShape: face.id, beard, goggles }];
}
